/** Parse un champ décimal du dialogue Approuver (virgule ou point). */
export function u3130ParseApproveDialogDecimal(raw: string): number {
  const t = raw.trim().replace(',', '.');
  if (t === '') return 0;
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

/** Écart en centièmes d’heure : somme des champs − total travaillé. */
export function u3130ComputeApproveDialogHoursCentiGap(
  totalStr: string,
  paidStr: string,
  unpaidStr: string,
  regularStr: string,
  halfStr: string,
  doubleStr: string,
): number {
  const total = u3130ParseApproveDialogDecimal(totalStr);
  const sum =
    u3130ParseApproveDialogDecimal(paidStr) +
    u3130ParseApproveDialogDecimal(unpaidStr) +
    u3130ParseApproveDialogDecimal(regularStr) +
    u3130ParseApproveDialogDecimal(halfStr) +
    u3130ParseApproveDialogDecimal(doubleStr);
  return Math.round(sum * 100) - Math.round(total * 100);
}

export function u3130IsApproveDialogHoursBalanced(
  totalStr: string,
  paidStr: string,
  unpaidStr: string,
  regularStr: string,
  halfStr: string,
  doubleStr: string,
): boolean {
  return (
    u3130ComputeApproveDialogHoursCentiGap(totalStr, paidStr, unpaidStr, regularStr, halfStr, doubleStr) === 0
  );
}

export function u3130FormatApproveHoursGapForLocale(centiGap: number, lang: string): string {
  const hours = centiGap / 100;
  const dec = lang.startsWith('fr') ? ',' : '.';
  const absPart = Math.abs(hours).toFixed(2).replace('.', dec);
  if (centiGap === 0) return `0${dec}00`;
  return hours > 0 ? `+${absPart}` : `−${absPart}`;
}

export type U3130ApproveDialogOpenFields = {
  totalHours: number;
  totalPaidHours: number;
  unpaidHours: number;
  regularHoursToBank: number;
  halfTimeHoursToBank: number;
  doubleTimeHoursToBank: number;
};

function roundHours2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Excédent au-dessus de la base horaire (BL-0637 — visibilité panneaux dialogue). */
export function u3130ApproveDialogHasHourlyExcess(
  hourlyBaseRaw: number | null | undefined,
  totalWorkedRaw: number,
): boolean {
  const totalH = roundHours2(Number.isFinite(totalWorkedRaw) ? totalWorkedRaw : 0);
  const baseNum =
    hourlyBaseRaw === null || hourlyBaseRaw === undefined || !Number.isFinite(Number(hourlyBaseRaw))
      ? 0
      : roundHours2(Number(hourlyBaseRaw));
  if (baseNum <= 0) {
    return totalH > 0;
  }
  return totalH > baseNum;
}

/** Afficher les panneaux horaire + salaire (excédent initial ou payées sous le travaillé). */
export function u3130ApproveDialogShowExcessPanels(
  hasExcess: boolean,
  totalStr: string,
  paidStr: string,
): boolean {
  if (hasExcess) return true;
  const totalCenti = Math.round(u3130ParseApproveDialogDecimal(totalStr) * 100);
  const paidCenti = Math.round(u3130ParseApproveDialogDecimal(paidStr) * 100);
  return paidCenti < totalCenti;
}

/** Préremplissage dialogue Approuver (BL-0634 — branche Horaire ; Salaire inchangé). */
export function u3130ComputeApproveDialogOpenFields(
  remunerationType: string | undefined | null,
  hourlyBaseRaw: number,
  totalWorkedRaw: number,
  existingBank?: Partial<
    Pick<U3130ApproveDialogOpenFields, 'regularHoursToBank' | 'halfTimeHoursToBank' | 'doubleTimeHoursToBank'>
  >,
): U3130ApproveDialogOpenFields {
  const hourlyBase = Number.isFinite(hourlyBaseRaw) ? hourlyBaseRaw : 0;
  const totalH = roundHours2(Number.isFinite(totalWorkedRaw) ? totalWorkedRaw : 0);
  const excess = roundHours2(Math.max(0, totalH - hourlyBase));
  const paidDefault = roundHours2(Math.min(hourlyBase, totalH));

  if (remunerationType === 'Salary') {
    return {
      totalHours: totalH,
      totalPaidHours: paidDefault,
      unpaidHours: excess,
      regularHoursToBank: 0,
      halfTimeHoursToBank: 0,
      doubleTimeHoursToBank: 0,
    };
  }
  if (remunerationType === 'Hourly') {
    return {
      totalHours: totalH,
      totalPaidHours: paidDefault,
      unpaidHours: 0,
      regularHoursToBank: excess,
      halfTimeHoursToBank: 0,
      doubleTimeHoursToBank: 0,
    };
  }
  return {
    totalHours: totalH,
    totalPaidHours: paidDefault,
    unpaidHours: excess,
    regularHoursToBank: existingBank?.regularHoursToBank ?? 0,
    halfTimeHoursToBank: existingBank?.halfTimeHoursToBank ?? 0,
    doubleTimeHoursToBank: existingBank?.doubleTimeHoursToBank ?? 0,
  };
}
